/* global google, DetectUserAgent */
import { useState, useEffect, useRef } from "react";

import { GOOGLE_BUTTON_SCRIPT_ID, GOOGLE_BUTTON_SCRIPT_SRC, UPLOAD_SOURCES } from "enums";
import { loadScriptByURL } from "utils/scriptsUtils";
import { jjLogger } from "utils/logUtils";
import { IS_GOOGLE_PHOTOS_ENABLED } from "utils/googlePhotosUtils";
import {
  initOAuth2Client,
  createPickerSession,
  pollSession,
  getSelectedMediaItems,
  getFirstSelectedMediaFile,
  isAnImageMediaFile,
  fetchMediaAsBase64,
  deleteSession,
  getToken,
  clearToken,
} from "utils/googlePhotosUtils";
import { rotateAndScale } from "utils/imageUtils";

import useNotifications from "./useNotifications";
import useTrackAccountEvents from "./useTrackAccountEvents";
import useUsers from "./useUsers";

const useGooglePhotos = ({ onPhotoSelected, templateGroup }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pickerUri, setPickerUri] = useState(null);
  const [googlePhotosSessionData, setGooglePhotosSessionData] = useState(null);
  const activeSessionIdRef = useRef(null);
  const googlePhotosTokenClientRef = useRef(null);

  const { addErrorNotification } = useNotifications();
  const { trackPhotoUploadEvents, trackClickPhotoUploadEvents } = useTrackAccountEvents();
  const { currentUser } = useUsers();

  const deletePickerSession = async (sessionId) => {
    try {
      await deleteSession(sessionId);
    } catch (deleteErr) {
      jjLogger.logError(`useGooglePhotos: deletePickerSession: Failed to delete session: ${deleteErr}`);
    }
  };

  const handleGooglePhotosToken = async (error) => {
    if (error) {
      addErrorNotification("Failed to authorize Google Photos access.");
      jjLogger.logError(`useGooglePhotos: handleGooglePhotosToken: OAuth error: ${error}`);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      const { pickerUri: sessionPickerUri, sessionId, pollingConfig } = await createPickerSession();
      const pickerUriWithAutoClose = `${sessionPickerUri}/autoclose`;
      activeSessionIdRef.current = sessionId;

      setPickerUri(pickerUriWithAutoClose);
      setGooglePhotosSessionData({
        sessionId,
        pollingConfig,
      });
    } catch (err) {
      if (err.message?.includes("401") || err.message?.includes("403") || err.message?.includes("missing or expired")) {
        clearToken();

        if (googlePhotosTokenClientRef.current) {
          googlePhotosTokenClientRef.current.requestAccessToken();
        }
      } else {
        addErrorNotification("Failed to create Google Photos session. Please try again.");
      }

      jjLogger.logError(`useGooglePhotos: handleGooglePhotosToken: ${err}`);

      if (activeSessionIdRef.current) {
        await deletePickerSession(activeSessionIdRef.current);
        activeSessionIdRef.current = null;
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const initGooglePhotos = async () => {
      try {
        await loadScriptByURL(GOOGLE_BUTTON_SCRIPT_ID, GOOGLE_BUTTON_SCRIPT_SRC);

        if (typeof google !== "undefined" && google?.accounts?.oauth2) {
          const client = initOAuth2Client(process.env.GOOGLE_LOGIN_CLIENT_ID, handleGooglePhotosToken);
          googlePhotosTokenClientRef.current = client;
        }
      } catch (error) {
        jjLogger.logError(`Failed to load Google Photos: ${error}`);
      }
    };

    if (IS_GOOGLE_PHOTOS_ENABLED && (DetectUserAgent.isInstagram4Android() || DetectUserAgent.isFB4Android())) {
      initGooglePhotos();
    }
  }, []);

  useEffect(() => {
    return () => {
      const sessionId = activeSessionIdRef.current;

      if (sessionId) {
        deleteSession(sessionId).catch((err) => {
          jjLogger.logError(`useGooglePhotos: cleanup - failed to delete session on unmount: ${err}`);
        });
      }
    };
  }, []);

  const resetGooglePhotosPickerSession = () => {
    activeSessionIdRef.current = null;
    setPickerUri(null);
    setGooglePhotosSessionData(null);
  };

  const handleClick = () => {
    trackClickPhotoUploadEvents(currentUser, UPLOAD_SOURCES.USE_GOOGLE_PHOTOS, templateGroup);

    if (!googlePhotosTokenClientRef.current) return;

    const storedToken = getToken();

    if (storedToken) {
      handleGooglePhotosToken(null);
      return;
    }

    googlePhotosTokenClientRef.current.requestAccessToken();
  };

  const handlePickerLinkClick = async () => {
    if (!googlePhotosSessionData) {
      jjLogger.logError("useGooglePhotos: handlePickerLinkClick: No session data available");
      addErrorNotification("Google Photos session not found. Please try again.");
      return;
    }

    const { sessionId, pollingConfig } = googlePhotosSessionData;
    let preservePickerSession = false;

    trackClickPhotoUploadEvents(currentUser, UPLOAD_SOURCES.OPEN_GOOGLE_PHOTOS, templateGroup);
    setIsLoading(true);

    try {
      await pollSession(sessionId, pollingConfig);

      const mediaItems = await getSelectedMediaItems(sessionId);
      const mediaFile = getFirstSelectedMediaFile(mediaItems);

      if (!isAnImageMediaFile(mediaFile)) {
        addErrorNotification("Only photos are supported. Please select a photo.");
        preservePickerSession = true;
        return;
      }

      trackPhotoUploadEvents(currentUser, UPLOAD_SOURCES.GOOGLE_PHOTOS, templateGroup);

      const base64 = await fetchMediaAsBase64(mediaFile);
      const rotatedAndScaled = await rotateAndScale(base64);

      onPhotoSelected(rotatedAndScaled);
    } catch (err) {
      jjLogger.logError(`useGooglePhotos: handlePickerLinkClick error: ${err}`);
      addErrorNotification("Failed to import from Google Photos. Please try again.");
    } finally {
      if (!preservePickerSession) {
        await deletePickerSession(sessionId);
        resetGooglePhotosPickerSession();
      }

      setIsLoading(false);
    }
  };

  return {
    isLoading,
    pickerUri,
    handleClick,
    handlePickerLinkClick,
  };
};

export default useGooglePhotos;
